// Market Session Utility for US Market Hours
// US Market: 9:30 AM - 4:00 PM ET, Monday-Friday

export interface MarketSession {
  isOpen: boolean;
  currentTime: string;
  marketOpen: string;
  marketClose: string;
  timeUntilOpen?: string;
  timeUntilClose?: string;
  sessionDate: string;
}

export const isUSMarketOpen = (): boolean => {
  try {
    // Get current time in Eastern Time
    const now = new Date();
    const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    
    // Market hours: 9:30 AM - 4:00 PM ET, Monday-Friday
    const marketOpen = new Date(easternTime);
    marketOpen.setHours(9, 30, 0, 0);
    
    const marketClose = new Date(easternTime);
    marketClose.setHours(16, 0, 0, 0);
    
    // Check if it's a weekday
    const isWeekday = easternTime.getDay() !== 0 && easternTime.getDay() !== 6;
    
    // Check if current time is within market hours
    const isMarketOpen = isWeekday && easternTime >= marketOpen && easternTime <= marketClose;
    
    return isMarketOpen;
  } catch (error) {
    console.error('Error checking market hours:', error);
    return false;
  }
};

export const getMarketSession = (): MarketSession => {
  try {
    const now = new Date();
    const easternTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    
    const marketOpen = new Date(easternTime);
    marketOpen.setHours(9, 30, 0, 0);
    
    const marketClose = new Date(easternTime);
    marketClose.setHours(16, 0, 0, 0);
    
    const isWeekday = easternTime.getDay() !== 0 && easternTime.getDay() !== 6;
    const isMarketOpen = isWeekday && easternTime >= marketOpen && easternTime <= marketClose;
    
    const formatTime = (date: Date) => date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'America/New_York'
    });
    
    const formatDate = (date: Date) => date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: 'America/New_York'
    });
    
    let timeUntilOpen: string | undefined;
    let timeUntilClose: string | undefined;
    
    if (!isMarketOpen) {
      if (easternTime < marketOpen) {
        const diff = marketOpen.getTime() - easternTime.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        timeUntilOpen = `${hours}h ${minutes}m`;
      } else {
        const diff = marketOpen.getTime() + (24 * 60 * 60 * 1000) - easternTime.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        timeUntilOpen = `${hours}h ${minutes}m`;
      }
    } else {
      const diff = marketClose.getTime() - easternTime.getTime();
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      timeUntilClose = `${hours}h ${minutes}m`;
    }
    
    return {
      isOpen: isMarketOpen,
      currentTime: formatTime(easternTime),
      marketOpen: formatTime(marketOpen),
      marketClose: formatTime(marketClose),
      timeUntilOpen,
      timeUntilClose,
      sessionDate: formatDate(easternTime)
    };
  } catch (error) {
    console.error('Error getting market session:', error);
    return {
      isOpen: false,
      currentTime: 'Unknown',
      marketOpen: '09:30',
      marketClose: '16:00',
      sessionDate: 'Unknown'
    };
  }
};

export const isNewMarketSession = (lastSessionDate?: string): boolean => {
  if (!lastSessionDate) return true;
  
  try {
    const currentSession = getMarketSession();
    return currentSession.sessionDate !== lastSessionDate;
  } catch (error) {
    console.error('Error checking new market session:', error);
    return false;
  }
};

/** Eastern calendar date as YYYY-MM-DD (US market timezone). */
export function getEasternDateYmd(d = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const day = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${day}`;
}

function easternWeekday(d = new Date()): number {
  const wd = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', weekday: 'short' }).format(d);
  const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return map[wd] ?? 0;
}

function shiftEasternYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  utc.setUTCDate(utc.getUTCDate() + deltaDays);
  return utc.toISOString().slice(0, 10);
}

/** Walk back to prior Mon–Fri session (skips weekends). */
export function getPreviousTradingSessionYmd(fromYmd?: string): string {
  let ymd = fromYmd || getEasternDateYmd();
  for (let i = 0; i < 10; i += 1) {
    ymd = shiftEasternYmd(ymd, -1);
    const probe = new Date(`${ymd}T12:00:00`);
    if (easternWeekday(probe) !== 0 && easternWeekday(probe) !== 6) return ymd;
  }
  return ymd;
}

/**
 * Default intraday session date: today when session is live; otherwise last completed session.
 * Weekday after 4pm ET → today (session finished). Weekend / pre-open → previous trading day.
 */
export function getDefaultIntradaySessionYmd(): string {
  if (isUSMarketOpen()) return getEasternDateYmd();
  const session = getMarketSession();
  const todayYmd = getEasternDateYmd();
  const wd = easternWeekday();
  if (wd >= 1 && wd <= 5) {
    const [hh, mm] = session.currentTime.split(':').map(Number);
    const mins = (hh || 0) * 60 + (mm || 0);
    if (mins >= 16 * 60) return todayYmd;
  }
  return getPreviousTradingSessionYmd(todayYmd);
}

/** Live polls / websocket ticks should run only during regular US session. */
export function shouldFeedLiveMarketData(): boolean {
  return isUSMarketOpen();
} 